<template>
    <div class="complete-verification-container">
        <p v-if="!isContingent && !isVerificationInProgress && identityValidationSuccess" class="sub-header">Next, set up your password and MFA to access Autodesk systems.   </p>
        <p v-if="!identityValidationSuccess" class="sub-header-for-failure">Complete verification</p>

        <transition name="fade">
            <div class="w-100">
                <div class="loader-container-progress w-100" :style="{ display: !isVerificationInProgress ? 'none' : 'block' }">
                    <div class="loader-item loader-desktop-item">
                        <identity-validation-loading-overlay v-if="isVerificationInProgress" />
                        <p v-if="isVerificationInProgress" class="identity-validation text-mobile-center identity-loader">Your identity
                            is being verified
                        </p>
                    </div>
                </div>
                <div v-if="!isVerificationInProgress && !identityValidationSuccess" class="handle-validation-failure">
                    <div class="identity-failure-container">
                        <div class="d-flex flex-row justify-content-start failure-container">
                            <img class="error-image"
                            src="../assets/images/error_icon.png" alt="Error Image" />
                                <p class="identity-validation mb-0 text-mobile-center">Something went wrong and your identity was not verified. Please try again and get help referring to this <a target="_blank" href="https://one.autodesk.com/IT/articles/15335" class="error-link ml-1"> document </a>.
                                </p>
                        </div>
                            <button @click="showManualVerification" class="btn flat-btn primary-blue-btn next-button m-0 verify-again-button">{{ isRetryExceeded ?
                            "Next" : "Verify identity" }}</button>
                    </div>
                </div>
            </div>
        </transition>

        <transition-group name="slide-fade">
            <div v-if="identityValidationSuccess" class="password-mfa-reset-container w-100 mt-0" key='verificationSucceded'>
                <div class="loader-container w-100" :style="{ display: mfaResetMySignInTrigger && !isVerificationInProgress ? 'block' : 'none' }">
                    <div class="loader-item mt-0">
                        <identity-validation-loading-overlay v-if="mfaResetMySignInTrigger && !isVerificationInProgress" />
                        <p v-if="mfaResetMySignInTrigger && !isVerificationInProgress" class="identity-validation text-mobile-center ml-3">Please wait while your MFA setup begins. This phase takes 3-4 minutes.
                        </p>
                    </div>
                </div>

                <div v-if="!isContingent" class="radio-options w-100 d-flex flex-row justify-content-between align-items-center">
                    <div class="action-wrapper d-flex flex-column justify-content-between" :style="{ opacity: mfaResetMySignInTrigger ? 0.5 : 1 }">
                        <div>
                            <h2 class="action-card-header"><strong>Set up password</strong></h2>
                            <p class="action-text">Click on the <b>Set up password</b> button to get started. You'll be redirected to a Microsoft site. When you're done creating your password, come back to here to set up your MFA.</p>
                        </div>
                        <div class="w-100 d-flex flex-row justify-content-end action-button-container">
                            <a class="link mr-2 ml-2 d-flex align-items-center mt-1" @click="validateSetupBasedOnAction('Password Reset')">View setup instructions</a>
                            <button class="btn flat-btn primary-blue-btn next-button cursor-pointer p-2" :disabled="mfaResetMySignInTrigger"
                                @click="handleResetAction('Password Reset')">Set up password</button>
                        </div>
                    </div>
                    <div class="action-wrapper d-flex flex-column justify-content-between" :style="{ opacity: !last_password_reset || mfaResetMySignInTrigger ? 0.5 : 1 }">
                        <div>
                            <h2 class="action-card-header"><strong>Set up Multi-factor Authentication</strong></h2>
                            <p class="action-text">To set up multi-factor authentication (MFA), you'll need your computer and a mobile device that you'll use to authenticate your Autodesk sign-ins. A personal device is acceptable. <br><br> When you click on <b>Set up MFA</b>, you'll be redirected to a Microsoft site to complete the process. </p>
                        </div>
                        <div class="w-100 d-flex flex-row justify-content-end action-button-container">
                            <a class="link mr-2 ml-2 d-flex align-items-center mt-1" @click="validateSetupBasedOnAction('MFA Reset')">View setup instructions</a>
                            <button :class="!last_password_reset || mfaResetMySignInTrigger ? 'btn flat-btn primary-blue-btn cancel-button cursor-pointer p-2' : 'btn flat-btn primary-blue-btn next-button cursor-pointer p-2'" :disabled="!last_password_reset || mfaResetMySignInTrigger"
                                @click="handleResetAction('MFA Reset')">Set up MFA</button>
                        </div>
                    </div>
                </div>
                <div v-if="isContingent && !isVerificationInProgress && identityValidationSuccess" class="loader-item verification-completed w-100 d-flex justify-content-center">
                    <div>
                        <font-awesome-icon class="checkmark" icon="check-circle" />
                    </div>
                    <p class="identity-validation completed">Your identity is now verified</p>
                    <div class="d-flex justify-content-center ml-2">
                        <button class="btn flat-btn form-next-button form-close-button ml-4" @click="redirectToLandingHome">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </transition-group>

        <div v-if="(identityValidationSuccess && showModal) && !isContingent" class="modal-overlay">
            <div class="modal-content m-3">
                <div class="d-flex flex-row justify-content-between">
                    <h2 class="modal-header p-0 pb-4 mb-0"><b>Check browser privacy settings</b></h2>
                    <button class="close ml-2" @click="closeModal">
                        <font-awesome-icon icon="times" />
                    </button>
                </div>
                <p class="popup-text">
                    Before you continue, please review your
                    <span class="link">browser privacy settings</span>.
                    Pop-ups and redirects must be enabled for the next steps.
                </p>
                <div class="modal-actions">
                    <button class="btn primary-flat-btn form-done-button" @click="closeModal">Done</button>
                </div>
            </div>
        </div>
        <div v-if="showPasswordSetupInstructions && showInstructionsModal" class="modal-overlay">
            <div class="modal-content next-steps m-3">
                <div class="d-flex flex-row justify-content-between">
                    <p class="pr-2">When you click on <b>Set up password</b>, you’ll be redirected to Microsoft’s Online Password reset tool. Here
                    are the
                    steps to follow:</p>
                    <button class="close" @click="closeModal">
                        <font-awesome-icon icon="times" />
                    </button>
                </div>
                <ul class="pl-3">
                    <li>Enter your Autodesk email address, {{ userEmail }}, complete the captcha, and click <b>Next</b>.
                    </li>
                    <li>Choose <b>Email my alternate email</b> and click <b>Get OTP</b>.</li>
                    <li>Go to the alternate email account to get your One Time Password (OTP) , enter it in your browser,
                        and click <b>Next</b>.</li>
                    <li>Set up your new password, confirm it, and click <b>Finish</b>.</li>
                </ul>
                <div class="modal-actions">
                    <button class="btn primary-flat-btn form-done-button" @click="closeModal">Close</button>
                </div>
            </div>
        </div>
        <div v-if="showMFASetupInstructions && showInstructionsModal" class="modal-overlay">
            <div class="modal-content next-steps m-3">
                <div class="d-flex flex-row justify-content-between">
                    <p class="pr-2">When you click on <b>Set up MFA</b>, you’ll be redirected to Microsoft's Mysign-in page. Here are the steps to follow:</p>
                    <button class="close" @click="closeModal">
                        <font-awesome-icon icon="times" />
                    </button>
                </div>
                <ul class="pl-3">
                    <li>On the next screen, sign in using your Autodesk email and new password.
                    </li>
                    <li>Follow the Set up your account prompts until you see a QR code on your screen.</li>
                    <li>On the mobile device you’ll use for authentication, download Microsoft Authenticator app. Open the
                        app, select <b>Add Account</b>, then <b>Work</b> or <b>School Account</b>.</li>
                    <li>If prompted, allow camera access and/or push notifications. They are both needed to set up your
                        account.</li>
                    <li>Select Scan QR Code and use your mobile device to scan the QR code on your computer, then click
                        <b>Next</b>.
                    </li>
                    <li>Enter the number displayed in the webpage and click <b>Approve</b> on the pop-up window on your
                        mobile
                        device.</li>
                    <li>Click Next on the webpage, then <b>Done</b>.
                    </li>
                </ul>
                <div class="modal-actions">
                    <button class="btn primary-flat-btn form-done-button" @click="closeModal">Close</button>
                </div>
            </div>
        </div>
        <loading-overlay v-if="isLoading"></loading-overlay>
    </div>
</template>

<script>
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import IdentityValidationLoadingOverlay from '@/components/IdentityValidationLoadingOverlay.vue';
import { resetPasswordAndMfa } from '@/api/index.js';
import CONSTANTS from "@/constants";

export default {
    name: "CompleteIdentityVerification",
    components: {
        LoadingOverlay,
        IdentityValidationLoadingOverlay
    },
    props: {
        emailValidationStatus: {
            type: String,
            required: true
        },
        identityValidationStatus: {
            type: String,
            required: true
        },
        isContingent: {
            type: Boolean,
            required: true
        },
        userEmail: {
            type: String,
            required: true
        },
        isRetryExceeded: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            isVerificationInProgress: false,
            identityValidationSuccess: null,
            selectedOption: "",
            warningMessages: CONSTANTS.WARNING_MESSAGES,
            mfaResetProcessingDelay: true,
            mfaResetMySignInTrigger: false,
            last_password_reset: false,
            last_mfa_reset: false,
            showModal: true,
            showInstructionsModal: false,
            showMFASetupInstructions: false,
            showPasswordSetupInstructions: false
        };
    },
    mounted() {
        this.simulateIdentityValidation();
        this.requestPasswordMFAReset("Reset Date");
        setTimeout(() => {
            this.$router.go(0);
        }, 30 * 60 * 1000);
    },
    methods: {
        showManualVerification() {
            this.isRetryExceeded ? this.$emit("showManualVerification") : this.$router.push('/');
        },
        closeModal() {
            this.$emit("close");
            this.showModal = false;
            this.showInstructionsModal = false;
            this.showMFASetupInstructions = false;
            this.showPasswordSetupInstructions = false;
        },
        validateSetupBasedOnAction(action) {
            this.showInstructionsModal = true;
            action == 'Password Reset' ? this.showPasswordSetupInstructions = true : 
            !this.last_password_reset || this.mfaResetMySignInTrigger ? 
            '' : this.showMFASetupInstructions = true;
        },
        initiateResetAction(option) {
            const url = option === 'Password Reset' ? "https://aka.ms/sspr" : "https://mysignins.microsoft.com/security-info";
            window.open(url, "_blank");
            this.selectedOption = ""
        },
        proceedToHome() {
            var baseUrl = window.location.origin;
            window.location.href = baseUrl + '/';
        },
        isResetDoneWithin24hrs(reset_date) {
            if (reset_date == null) return false;
            const currentDateTime = new Date();
            const lastResetutcTime = new Date(reset_date).getTime();
            const lastResetDateIST = new Date(lastResetutcTime + (5.5 * 60 * 60 * 1000));
            const differenceInHours = (currentDateTime - lastResetDateIST) / (1000 * 60 * 60);
            return differenceInHours <= 24;
        },
        async requestPasswordMFAReset(action) {
            const response = await resetPasswordAndMfa(this.userEmail, action);
            const data = await response.json();
            if (action == "Password Reset") {
                this.initiateResetAction(action);
                this.last_password_reset = true
            } else if (action == "MFA Reset") {
                this.mfaResetMySignInTrigger = true;
                setTimeout(() => {
                    this.mfaResetProcessingDelay = false;
                    this.mfaResetMySignInTrigger = false;
                    this.initiateResetAction(action);
                    this.last_mfa_reset = true
                }, 4 * 60 * 1000);
            } else {
                if(data.last_reset_date) {
                    this.last_password_reset = this.isResetDoneWithin24hrs(data.last_reset_date['Password Reset']);
                    this.last_mfa_reset = this.isResetDoneWithin24hrs(data.last_reset_date['MFA Reset']);
                }
            }
            this.isLoading = false;
        },
        simulateIdentityValidation() {
            this.isVerificationInProgress = true;
            setTimeout(() => {
                this.isVerificationInProgress = false;
                this.identityValidationSuccess = (this.emailValidationStatus === "Validated" && this.identityValidationStatus === "Verified");
                if (this.identityValidationSuccess) {
                    this.$emit("nextStep", 4);
                }
            }, 4000);
        },
        handleResetAction(action) {
            try {
                this.isLoading = true;
                this.requestPasswordMFAReset(action);
                this.isVerificationInProgress = false;
            } catch (error) {
                this.isLoading = false;
            }
        },
        redirectToLandingHome() {
            const url = CONSTANTS.LANDING_URL
            window.open(url, "_self");
        }
    }
};
</script>

<style scoped>
.complete-verification-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: auto;
    max-width: 1200px;
    color: black;
    font-family: artifaktElementRegular;
}

.handle-validation-failure {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    min-height: 180px;
    width: 100%;
    padding : 5px 10px;
}

.form-next-button:disabled {
    background-color: #ccc;
    color: #fff;
    cursor: not-allowed;
}

.next-button {
    text-transform: none;
    min-width: 80px !important;
    height: 40px;
    color: white;
    background-color: #000000;
    border: none;
    border-radius: 4px !important;
    font-weight: bolder;
    max-width: 180px;
}

.btn:disabled {
    opacity: 0.35 !important;
    cursor: not-allowed;
}

.action-card-header {
    font-size: 18px;
    font-weight: bolder;
    font-family: artifaktElementBold;
}

.popup-text {
    font-size: 14px;
}

.action-wrapper {
    width: 48%;
    height: 300px;
    padding: 10px 20px;
    border: 1px solid #000000;
    border-radius: 5px;
}

.action-header {
    font-size: 16px;
    font-family: artifaktElementBold;
    margin-left: 10px;
}

.action-button-container {
    border-top: 1px solid gray;
    padding-top: 10px;
}

.action-label {
    font-size: 14px;
}

.sub-header {
    padding-left: 10px;
    font-size: 17px;
    font-weight: bold;
    margin-top: 30px;
}

.identity-loader {
    font-size: 16px !important;
    margin-top: 20px !important;
}

.step-label.completed,
.step-label.active,
.email-validation {
    display: none;
}

.identity-failure-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 20px;
    margin-top: 20px;
    padding: 5px 10px;
    justify-content: space-between;
    width: 100%;
    font-size: 14px;
    border: 1px solid #CC4B24;
    background-color: #FAEDE9;
}

.error-image {
    width: 30px;
    margin-right: 10px;
}

.next-steps {
    width: 600px !important;
    min-width: 200px;
    max-width: 600px;
    padding: 10px;
    border-radius: 4px;
    font-size: 14px;
}

.cancel-button {
    color: #000000 !important;
    background-color: white !important;
    min-width: 80px !important;
    height: 40px;
    text-transform: capitalize;
    border-radius: 4px !important;
    font-weight: bolder;
    border: 1px solid gray !important;
    max-width: 180px;
}
.action-text {
    font-size: 14px;
}

.next-button {
    text-transform: none;
    min-width: 80px !important;
    height: 40px;
    color: white;
    border: none;
    border-radius: 4px !important;
}

.btn:disabled {
    opacity: 0.35 !important;
}

.password-info-list {
    list-style: none;
    list-style-position: inside;
    padding-left: 20px;
}

.password-info-list li {
    margin-bottom: 10px;
    text-align: start;
}

.password-info-list li::before {
    margin-right: 10px;
}

.close {
    font-size: 20px;
    padding-bottom: 20px;
}

.password-info-list li::marker {
    color: #5a52d1;
    margin-right: 0px !important;
}

.next-button:hover:not(:disabled) {
    background-color: #5a52d1;
}

.header {
    font-size: 20px;
    font-weight: bolder;
    color: black;
    font-family: sans-serif;
    margin: 0px;
    margin-bottom: 20px;
    padding: 30px 10px;
}

.loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.loader-container-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 60px;
}

.loader {
    margin-top: 20px;
    margin-right: 10px;
}

.checkmark.completed {
    font-size: 42px;
}

.loader-item {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.loader-desktop-item {
    margin-top: 20px;
}

.loader-item p {
    margin: 0;
    margin-left: 10px;
    font-family: artifaktElementRegular;
}

.checkmark {
    color: green;
    font-size: 30px;
}

.crossmark {
    color: red;
    font-size: 24px;
}

.identity-validation {
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
}

.sub-header-for-failure {
    display: none;
}
.complete-verification-container span {
    width: 100% !important;
}
.next-button {
    text-transform: none;
    min-width: 80px !important;
    height: 40px;
    color: white;
    background-color: #000000;
    border: none;
    border-radius: 4px !important;
    font-weight: bolder;
    max-width: 180px;
}
.btn:disabled {
    opacity: 0.35 !important;
    cursor: not-allowed;
}
.action-card-header {
    font-size: 20px;
    font-weight: bolder;
    font-family: artifaktElementBold;
}
.popup-text {
    font-size: 14px;
}
.action-wrapper {
    width: 48%;
    height: 300px;
    padding: 10px 20px;
    border: 1px solid #000000;
    border-radius: 5px;
}

.error-message {
    color: red;
    font-size: 16px;
    font-weight: 700;
    margin-top: 10px;
}

.disabled {
    pointer-events: none;
    opacity: 0.5;
}

.reset-password-header {
    font-size: 18px;
    font-weight: 700;
    color: black;
    font-family: artifaktElementBold;
    line-height: 34px;
    margin: 0px;
}

.password-mfa-reset-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 20px;
    padding: 0px 10px;
}

.radio-class {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;
    width: 16px !important;
    height: 16px;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
}

.radio-class:checked {
    background-color: #5f60ff;
    border: none !important;
}

.radio-class::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.modal {
    background: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
    margin-top: 10%;
    margin-top: calc(15vh - 50px);
}

.radio-options {
    display: flex;
    flex-direction: column;
}

.radio-options label {
    margin-bottom: 10px;
}

.verification-completed {
    margin-top: 70px;
    margin-right: 70px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 1s;
}

.password-reset-container {
    font-family: artifaktElementRegular;
}

.modal-body p {
    color: black;
    font-size: 15px;
    text-align: start;
    font-family: artifaktElementRegular;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.slide-fade-enter-active {
    transition: all 0.5s ease;
}

.slide-fade-leave-active {
    transition: all 0.5s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  z-index: 1000;
}

.modal-header {
    font-size: 18px;
    border-bottom: none !important;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 4px;
  width: 400px;
  position: relative;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  text-align: left;
  font-size: 14px;
}

.link {
    color: #000000 !important;
    word-break: break-all;
    text-decoration: none;
    font-size: 14px;
}

.error-link {
    color: red !important;
    word-break: break-all;
    text-decoration: none;
    font-size: 14px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
}

.form-next-button {    
    color: white;
    padding: 6px 40px;
    border: none;
    border-radius: 40px;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
}

.form-close-button {
    margin-top: 0px;
    color: #000000 !important;
    background-color: white;
    min-width: 80px !important;
    height: 40px;
    text-transform: capitalize;
    border-radius: 4px !important;
    font-weight: bolder;
}

.form-close-button {
    background-color: transparent !important;
    color: inherit !important;
}

.form-done-button {
    color: white !important;
    background-color: #000000;
    min-width: 80px !important;
    height: 40px;
    text-transform: capitalize;
    border-radius: 4px !important;
    font-weight: bolder;
}

.form-next-button:hover:not(:disabled) {
    background-color: #5a52d1;
}

.radio-options label {
    display: flex;
}

.radio-options input[type="radio"] {
    margin-right: 16px;
    border: 1px solid black;
}

.radio-options label p {
    font-weight: bold;
    margin-left: 10px;
}

@media ((min-width: 700px) and (max-width : 850px)) {
    .action-wrapper {
        height: 400px;
    }
}

@media ((max-width : 700px)) {
    .identity-loader {
        margin-top: 0px !important;
    }
    .failure-container {
        align-items: center;
    }
    .modal-header {
        font-size: 16px;
    }

    .email-validation {
        margin-bottom: 30px;
    }

    .header {
        display: none;
    }

    .handle-validation-failure {
        min-height: 100px;
    }

    .sub-header-for-failure {
        display: block;
        margin: 0px;
        margin-top: 20px;
        font-size: 18px;
        font-family: artifaktElementBold;
        padding-left: 10px;
    }
    .modal-overlay {
        padding-top: 0px;
    }

    .action-label {
        width: 80%;
    }

    .failure-container {
        flex-direction: column !important;
    }

    .action-button-container {
        flex-direction: column-reverse !important;
        align-items: center;
    }
    .verify-again-button {
        margin-top: 20px !important;
        max-width: 180px !important;
    }

    .loader-item {
        flex-direction: column;
    }

    .text-mobile-center {
        margin-top: 10px;
        text-align: center;
    }

    .failure-container {
        flex-direction: column !important;
    }

    .identity-failure-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        height: auto;
        margin-top: 20px;
    }

    .verification-completed {
        margin-top: 70px;
    }

    .error-image {
        width: 30px;
        height: 30px;
        margin: 0px;
    }

    .form-next-button {
        margin-left: 0px !important;
    }

    .form-close-button {
        margin-top: 20px !important;
    }

    .next-button, .cancel-button {
        max-width: 100%;
        margin-bottom: 10px;
    }

    .identity-validation.completed {
        text-align: center;
    }

    .checkmark {
        color: green;
        font-size: 30px;
    }

    .password-mfa-reset-container {
        margin-top: 0px;
        padding: 0px !important;
    }

    .sub-header {
        margin-top: 20px;
        padding: 0px;
        font-size: 16px;
    }
    .radio-options {
        flex-direction: column !important;
        width: 100%;
    }
    .action-wrapper {
        width: 100%;
        margin-bottom: 20px;
        height: auto;
        max-width: 400px;
    }

    .sub-header {
        margin-top: 0px !important;
    }

    .radio-options {
        flex-direction: column !important;
        width: 100%;
    }

    .action-wrapper {
        width: 100%;
        margin-bottom: 20px;
        height: auto;
    }

    .loader-container-progress {
        margin-top: 30px;
    }

}
</style>